import React from 'react';
import { Button, Dropdown, Input, MessageBarActions, MessageBarBody, MessageBarTitle, Option } from '@fluentui/react-components';
import { ArrowClockwise20Regular, Delete20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid, GuideHelpText } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';

import {
  confidencePercent,
  formatImportAmount,
  formatImportDate,
  getApiErrorMessage,
  ImportMappingForm,
  isDocumentStrategy,
  isLowConfidence,
  isValidCurrencyCode,
  mappingColumnOptions,
} from '../../../functions/ProviderConsumptionImport.Functions';
import {
  IMPORT_MAPPING_FIELDS,
  ImportMappingField,
  ImportPreview,
  ImportPreviewItem,
  LAST_PRICE_COLUMN,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';

/** Valor de la opción "(ninguna)" en los desplegables de columna. */
export const NO_COLUMN_OPTION = '__none__';
/** Valor de la opción "Última columna de precio" en el desplegable de valor. */
export const LAST_COLUMN_OPTION = '__last__';

/** Cantidad máxima de avisos generales que se listan. */
export const MAX_PREVIEW_WARNINGS = 10;

export interface ImportMappingPreviewProps {
  preview: ImportPreview | undefined;
  loading: boolean;
  /** Error de la última vista previa (con la vista anterior todavía en pantalla, si había). */
  error?: unknown;
  form: ImportMappingForm;
  onFormChange: (form: ImportMappingForm) => void;
  /** Hay cambios del mapeo que todavía no se recalcularon. */
  dirty: boolean;
  /** Bloquea la edición (importando / eliminando). */
  busy: boolean;
  onRecalculate: () => void;
  onRetry: () => void;
  /** "Eliminar consumos importados de este archivo" (abre la confirmación). Sin callback no se ofrece. */
  onRequestDelete?: () => void;
  deleting?: boolean;
  /** Resultado de la última eliminación (cantidad). */
  deletedCount?: number;
  deleteError?: unknown;
}

const optionValueOf = (field: ImportMappingField, value: string | null): string => {
  if (value === null) return NO_COLUMN_OPTION;
  if (field === 'value' && value.toLowerCase() === LAST_PRICE_COLUMN) return LAST_COLUMN_OPTION;
  return value;
};

const valueOfOption = (option: string | undefined): string | null => {
  if (!option || option === NO_COLUMN_OPTION) return null;
  if (option === LAST_COLUMN_OPTION) return LAST_PRICE_COLUMN;
  return option;
};

/**
 * Paso 2 del asistente "Importar consumos" (spec 007): plan de normalización de la IA (estrategia, hoja,
 * encabezado, confianza, notas), mapeo editable columna → campo, fecha del documento, moneda por defecto,
 * las primeras filas normalizadas y el estado de importaciones previas (conflicto de mapeo + eliminar).
 */
export const ImportMappingPreview = (props: ImportMappingPreviewProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.mapping.${key}`, options);
  const { preview, form } = props;

  if (props.loading && !preview) return <GuideSkeleton rows={6} label={tr('loading')} data-testid="pci-preview-loading" />;

  const errorBanner =
    props.error !== undefined ? (
      <GuideMessageBar intent="error" layout="multiline" data-testid="pci-preview-error">
        <MessageBarBody>
          <MessageBarTitle>{tr('error')}</MessageBarTitle>
          {getApiErrorMessage(props.error)}
        </MessageBarBody>
        <MessageBarActions>
          <GuideBannerAction onClick={props.onRetry}>{tr('retry')}</GuideBannerAction>
        </MessageBarActions>
      </GuideMessageBar>
    ) : null;

  if (!preview) return errorBanner;

  const normalization = preview.normalization ?? null;
  const degraded = normalization === null;
  const documentStrategy = isDocumentStrategy(normalization);
  const columnOptions = mappingColumnOptions(
    preview.availableColumns,
    IMPORT_MAPPING_FIELDS.map((field) => (field === 'value' && form.columns.value?.toLowerCase() === LAST_PRICE_COLUMN ? null : form.columns[field]))
  );
  const columnsEditable = !degraded && !documentStrategy && (preview.availableColumns ?? []).length > 0;
  const currencyInvalid = !isValidCurrencyCode(form.defaultCurrencyCode);
  const items = preview.items ?? [];
  const warnings = preview.warnings ?? [];
  const notes = normalization?.notes ?? [];
  const alreadyImported = Number(preview.alreadyImportedCount ?? 0);
  const planConfidence = confidencePercent(normalization?.confidence);
  const disabled = props.busy || props.loading;

  const setColumn = (field: ImportMappingField, value: string | null) =>
    props.onFormChange({ ...form, columns: { ...form.columns, [field]: value } });

  const columnLabel = (field: ImportMappingField, value: string | null): string => {
    if (value === null) return tr('noColumn');
    if (field === 'value' && value.toLowerCase() === LAST_PRICE_COLUMN) return tr('lastPriceColumn');
    return value;
  };

  const renderPlan = () => {
    if (degraded) {
      return (
        <GuideMessageBar intent="info" layout="multiline" data-testid="pci-preview-degraded">
          <MessageBarBody>
            <MessageBarTitle>{tr('degradedTitle')}</MessageBarTitle>
            {tr('degradedText')}
          </MessageBarBody>
        </GuideMessageBar>
      );
    }
    return (
      <dl className="pci-plan" data-testid="pci-preview-plan">
        <div>
          <dt>{tr('strategy')}</dt>
          <dd>
            <StatusPill tone={documentStrategy ? 'confirmed' : 'info'} data-testid="pci-preview-strategy">
              {documentStrategy ? tr('strategyDocument') : tr('strategyTabular')}
            </StatusPill>
          </dd>
        </div>
        {!documentStrategy && (
          <>
            <div>
              <dt>{tr('sheet')}</dt>
              <dd data-testid="pci-preview-sheet">{normalization?.sheetName || '—'}</dd>
            </div>
            <div>
              <dt>{tr('headerRow')}</dt>
              <dd className="ichtys-num" data-testid="pci-preview-header-row">
                {normalization?.headerRowIndex
                  ? Number(normalization.headerRowCount ?? 1) > 1
                    ? tr('headerRowsValue', { from: normalization.headerRowIndex, to: normalization.headerRowIndex + Number(normalization.headerRowCount) - 1 })
                    : normalization.headerRowIndex
                  : '—'}
              </dd>
            </div>
          </>
        )}
        <div>
          <dt>{tr('confidence')}</dt>
          <dd data-testid="pci-preview-confidence">
            {planConfidence === undefined ? (
              '—'
            ) : (
              <StatusPill tone={isLowConfidence(normalization?.confidence) ? 'warning' : 'success'}>{tr('percent', { value: planConfidence })}</StatusPill>
            )}
          </dd>
        </div>
        <div>
          <dt>{tr('totalRows')}</dt>
          <dd className="ichtys-num" data-testid="pci-preview-total">
            {Number(preview.totalRows ?? 0).toLocaleString()}
          </dd>
        </div>
      </dl>
    );
  };

  const renderColumnField = (field: ImportMappingField) => {
    const value = form.columns[field];
    const selected = optionValueOf(field, value);
    return (
      <GuideField key={field} label={tr(`fields.${field}`)}>
        <Dropdown
          value={columnLabel(field, value)}
          selectedOptions={[selected]}
          disabled={disabled}
          onOptionSelect={(_event, data) => setColumn(field, valueOfOption(data.optionValue))}
          data-testid={`pci-map-${field}`}
        >
          <Option value={NO_COLUMN_OPTION} text={tr('noColumn')}>
            {tr('noColumn')}
          </Option>
          {field === 'value' && (
            <Option value={LAST_COLUMN_OPTION} text={tr('lastPriceColumn')}>
              {tr('lastPriceColumn')}
            </Option>
          )}
          {columnOptions.map((column) => (
            <Option key={column} value={column} text={column}>
              {column}
            </Option>
          ))}
        </Dropdown>
      </GuideField>
    );
  };

  const renderReadOnlyColumns = () => {
    const source = degraded ? preview.detectedColumns ?? {} : form.columns;
    const fields = IMPORT_MAPPING_FIELDS.filter((field) => !degraded || field in source);
    return (
      <dl className="pci-plan pci-plan--columns" data-testid="pci-map-readonly">
        {fields.map((field) => (
          <div key={field}>
            <dt>{tr(`fields.${field}`)}</dt>
            <dd>{source[field] ? columnLabel(field, source[field] ?? null) : tr('noColumn')}</dd>
          </div>
        ))}
      </dl>
    );
  };

  const renderMapping = () => (
    <GuideDrawerSection
      title={tr('mappingTitle')}
      description={documentStrategy ? tr('mappingDocumentDescription') : tr('mappingDescription')}
      actions={
        degraded ? undefined : (
          <Button
            appearance={props.dirty ? 'primary' : 'secondary'}
            icon={<ArrowClockwise20Regular />}
            onClick={props.onRecalculate}
            disabled={disabled || currencyInvalid || !props.dirty}
            aria-busy={props.loading || undefined}
            data-testid="pci-recalc"
          >
            {props.loading ? tr('recalculating') : tr('recalculate')}
          </Button>
        )
      }
      testId="pci-mapping-section"
    >
      {!documentStrategy && (columnsEditable ? <GuideFieldGrid data-testid="pci-map-grid">{IMPORT_MAPPING_FIELDS.map(renderColumnField)}</GuideFieldGrid> : renderReadOnlyColumns())}
      {!degraded && !documentStrategy && !columnsEditable && <GuideHelpText data-testid="pci-map-no-columns">{tr('noAvailableColumns')}</GuideHelpText>}
      {!degraded && (
        <GuideFieldGrid>
          <GuideField label={tr('documentDate')} hint={tr('documentDateHint')}>
            <Input
              type="date"
              value={form.documentDate ?? ''}
              disabled={disabled}
              onChange={(_event, data) => props.onFormChange({ ...form, documentDate: data.value || null })}
              data-testid="pci-map-document-date"
            />
          </GuideField>
          <GuideField label={tr('defaultCurrency')} hint={tr('defaultCurrencyHint')} error={currencyInvalid ? tr('currencyInvalid') : undefined}>
            <Input
              value={form.defaultCurrencyCode ?? ''}
              maxLength={3}
              disabled={disabled}
              onChange={(_event, data) => props.onFormChange({ ...form, defaultCurrencyCode: data.value ? data.value.toUpperCase() : null })}
              data-testid="pci-map-default-currency"
            />
          </GuideField>
        </GuideFieldGrid>
      )}
      {props.dirty && !degraded && (
        <GuideMessageBar intent="info" layout="multiline" data-testid="pci-map-dirty">
          <MessageBarBody>{tr('dirty')}</MessageBarBody>
        </GuideMessageBar>
      )}
    </GuideDrawerSection>
  );

  const renderConfidence = (item: ImportPreviewItem) => {
    const percent = confidencePercent(item.confidence);
    if (percent === undefined) return <span className="pci-secondary-cell">—</span>;
    return isLowConfidence(item.confidence) ? (
      <StatusPill tone="warning" data-testid={`pci-preview-low-${item.rowNumber}`}>
        {tr('lowConfidence', { value: percent })}
      </StatusPill>
    ) : (
      <span className="ichtys-num">{tr('percent', { value: percent })}</span>
    );
  };

  const renderRows = () => (
    <GuideDrawerSection
      title={tr('rowsTitle')}
      description={tr('rowsDescription', { shown: items.length, total: Number(preview.totalRows ?? 0).toLocaleString() })}
      testId="pci-rows-section"
    >
      {props.loading ? (
        <GuideSkeleton rows={4} label={tr('loading')} data-testid="pci-preview-reloading" />
      ) : items.length === 0 ? (
        <GuideHelpText data-testid="pci-preview-no-rows">{tr('noRows')}</GuideHelpText>
      ) : (
        <GuideTable ariaLabel={tr('rowsTitle')} data-testid="pci-preview-table" scrollTestId="pci-preview-table-scroll">
          <thead>
            <tr>
              <GuideTh>{tr('columns.row')}</GuideTh>
              <GuideTh>{tr('columns.code')}</GuideTh>
              <GuideTh>{tr('columns.name')}</GuideTh>
              <GuideTh>{tr('columns.date')}</GuideTh>
              <GuideTh align="end">{tr('columns.quantity')}</GuideTh>
              <GuideTh align="end">{tr('columns.amount')}</GuideTh>
              <GuideTh>{tr('columns.patient')}</GuideTh>
              <GuideTh>{tr('columns.confidence')}</GuideTh>
              <GuideTh>{tr('columns.warnings')}</GuideTh>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const itemWarnings = item.warnings ?? [];
              return (
                <tr
                  key={`${item.rowNumber}-${item.sourceReference ?? ''}`}
                  className={guideRowClassName({ pending: isLowConfidence(item.confidence) || itemWarnings.length > 0 })}
                  data-testid={`pci-preview-row-${item.rowNumber}`}
                  data-low-confidence={isLowConfidence(item.confidence) || undefined}
                >
                  <td className="pci-nowrap">
                    <span className="ichtys-num">{item.rowNumber}</span>
                    {item.sourceReference && <span className="pci-secondary">{item.sourceReference}</span>}
                  </td>
                  <td className="ichtys-num">{item.code || '—'}</td>
                  <td>{item.name || '—'}</td>
                  <td className="ichtys-num pci-nowrap">{formatImportDate(item.serviceDate)}</td>
                  <td className={cell('end', 'num')}>{item.quantity ?? 1}</td>
                  <td className={cell('end', 'num')}>
                    <span className="pci-nowrap">{formatImportAmount(item.amount, item.currencyCode)}</span>
                    {item.rawAmount && <span className="pci-secondary">{item.rawAmount}</span>}
                  </td>
                  <td>{item.patientReference || <span className="pci-secondary-cell">—</span>}</td>
                  <td>{renderConfidence(item)}</td>
                  <td>
                    {itemWarnings.length === 0 ? (
                      <span className="pci-secondary-cell">—</span>
                    ) : (
                      <ul className="pci-list pci-list--compact">
                        {itemWarnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </GuideTable>
      )}
    </GuideDrawerSection>
  );

  return (
    <div className="pci-stack" data-testid="pci-preview">
      {errorBanner}
      {props.deleteError !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-delete-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('deleteError')}</MessageBarTitle>
            {getApiErrorMessage(props.deleteError)}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      {props.deletedCount !== undefined && (
        <GuideMessageBar intent="success" layout="multiline" data-testid="pci-delete-result">
          <MessageBarBody>{tr('deleted', { count: props.deletedCount })}</MessageBarBody>
        </GuideMessageBar>
      )}
      {preview.mappingConflict ? (
        <GuideMessageBar intent="warning" layout="multiline" data-testid="pci-preview-conflict">
          <MessageBarBody>
            <MessageBarTitle>{tr('conflictTitle')}</MessageBarTitle>
            {tr('conflictText', { count: alreadyImported })}
          </MessageBarBody>
          {props.onRequestDelete && (
            <MessageBarActions>
              <Button
                appearance="secondary"
                icon={<Delete20Regular />}
                onClick={props.onRequestDelete}
                disabled={disabled || props.deleting}
                data-testid="pci-delete-imported"
              >
                {tr('deleteImported')}
              </Button>
            </MessageBarActions>
          )}
        </GuideMessageBar>
      ) : alreadyImported > 0 ? (
        <GuideMessageBar intent="info" layout="multiline" data-testid="pci-preview-already">
          <MessageBarBody>
            <MessageBarTitle>{tr('alreadyTitle')}</MessageBarTitle>
            {tr('alreadyText', { count: alreadyImported })}
          </MessageBarBody>
        </GuideMessageBar>
      ) : null}
      <GuideDrawerSection title={tr('planTitle')} description={preview.fileName || undefined} testId="pci-plan-section">
        {renderPlan()}
        {degraded && renderReadOnlyColumns()}
        {notes.length > 0 && (
          <ul className="pci-list pci-notes" aria-label={tr('notes')} data-testid="pci-preview-notes">
            {notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        )}
        {warnings.length > 0 && (
          <GuideMessageBar intent="warning" layout="multiline" data-testid="pci-preview-warnings">
            <MessageBarBody>
              <MessageBarTitle>{tr('warningsTitle', { count: warnings.length })}</MessageBarTitle>
              <ul className="pci-list">
                {warnings.slice(0, MAX_PREVIEW_WARNINGS).map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
              {warnings.length > MAX_PREVIEW_WARNINGS && (
                <span className="pci-secondary">{t('providerConsumptionImport.importDrawer.moreWarnings', { count: warnings.length - MAX_PREVIEW_WARNINGS })}</span>
              )}
            </MessageBarBody>
          </GuideMessageBar>
        )}
      </GuideDrawerSection>
      {!degraded && renderMapping()}
      {renderRows()}
    </div>
  );
};

export default ImportMappingPreview;
